import { DetailRow } from "@/components/shared/finance/DetailRow";
import { MonoAmount } from "@/components/shared/finance/MonoAmount";
import { MonoId } from "@/components/shared/finance/MonoId";
import {
  createPeekContent,
  RecentDocumentsList,
} from "@/components/shared/relationships/peek-content";
import { registerPeekType } from "@/components/shared/relationships/peek-registry";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useAccountRelated } from "@/features/accounts/hooks/use-accounts";
import { ABSENT_VALUE, formatCurrency } from "@/lib/format";

function AccountContent({
  related,
}: {
  related: NonNullable<ReturnType<typeof useAccountRelated>["data"]>;
}) {
  const { account } = related;

  return (
    <>
      <div className="space-y-1">
        <div className="font-semibold text-lg">{account.name ?? ABSENT_VALUE}</div>
        <MonoId>{account.uid}</MonoId>
        {account.type ? <Badge variant="outline">{account.type}</Badge> : null}
      </div>
      <Separator />
      <div className="space-y-1">
        <DetailRow
          label="Balance"
          value={<MonoAmount>{formatCurrency(account.balance)}</MonoAmount>}
        />
        <DetailRow
          label="Customer Balance"
          value={<MonoAmount>{formatCurrency(account.customer_balance)}</MonoAmount>}
        />
        <DetailRow
          label="Vendor Balance"
          value={<MonoAmount>{formatCurrency(account.vendor_balance)}</MonoAmount>}
        />
        <DetailRow
          label="Credit Balance"
          value={<MonoAmount>{formatCurrency(related.account_credit_balance)}</MonoAmount>}
        />
      </div>
      <Separator />
      <RecentDocumentsList
        emptyMessage="No invoices found"
        items={related.invoices}
        title="Recent Invoices"
        type="invoice"
      />
      <RecentDocumentsList
        emptyMessage="No purchase orders found"
        items={related.purchase_orders}
        title="Recent Purchase Orders"
        type="purchase-order"
      />
    </>
  );
}

/** Account quick-view content, registered on import via `src/app/peek-registrations.ts`. */
export const AccountPeekContent = createPeekContent({
  emptyMessage: "No account found",
  render: (related) => <AccountContent related={related} />,
  useDetail: useAccountRelated,
});

registerPeekType("account", AccountPeekContent);
